import { MutableRefObject, useCallback, useState } from 'react'
import { defaultEditorTheme } from '../../templateEditor.theme'
import { normalizeBroadcastDocument } from '../../templateEditor.document'
import { BroadcastTemplateDocument, TemplateElement } from '../../templateEditor.types'
import { useTemplateEditorCanvas } from './useTemplateEditorCanvas'
import { useTemplateEditorElementOps } from './useTemplateEditorElementOps'
import { useTemplateEditorGuides } from './useTemplateEditorGuides'
import { useTemplateEditorHistory } from './useTemplateEditorHistory'
import { useTemplateEditorInlineEdit } from './useTemplateEditorInlineEdit'
import { useTemplateEditorLayers } from './useTemplateEditorLayers'
import { useTemplateEditorSelection } from './useTemplateEditorSelection'
import { useTemplateEditorShortcuts } from './useTemplateEditorShortcuts'
import { useTemplateEditorTransform } from './useTemplateEditorTransform'

const theme = defaultEditorTheme

export const useTemplateEditorDocument = (zoomRef: MutableRefObject<number>) => {
    const [elements, setElements] = useState<TemplateElement[]>([])
    const [selectedIds, setSelectedIds] = useState<string[]>([])

    const guides = useTemplateEditorGuides()
    const history = useTemplateEditorHistory(elements, selectedIds, setElements, setSelectedIds)
    const canvas = useTemplateEditorCanvas({ setElements, setSelectedIds, history })
    const selection = useTemplateEditorSelection({ elements, selectedIds, setSelectedIds, zoomRef })
    const elementOps = useTemplateEditorElementOps({
        canvasW: canvas.canvasW,
        canvasH: canvas.canvasH,
        setElements,
        history,
    })
    const { updateElementRect, ...elementOpsPublic } = elementOps
    const transform = useTemplateEditorTransform({
        canvasW: canvas.canvasW,
        canvasH: canvas.canvasH,
        showGrid: canvas.showGrid,
        gridSize: canvas.gridSize,
        elements,
        selectedIds,
        setElements,
        setSelectedIds,
        history,
        guides,
        zoomRef,
        patchElement: elementOps.patchElement,
        updateElementRect,
    })
    const layers = useTemplateEditorLayers({ elements, selectedIds, setElements, history })

    const pushHistorySnapshot = useCallback(() => {
        history.pushHistory()
    }, [history])

    const inlineEdit = useTemplateEditorInlineEdit({
        setElements,
        setSelectedIds,
        history,
    })

    const applyDocument = useCallback(
        (document: BroadcastTemplateDocument) => {
            const normalized = normalizeBroadcastDocument(document)
            history.clearHistory()
            canvas.hydrateCanvas(normalized.canvas)
            setElements(normalized.elements)
            setSelectedIds([])
        },
        [canvas.hydrateCanvas, history.clearHistory]
    )

    useTemplateEditorShortcuts({
        history,
        moveSelectedBy: transform.moveSelectedBy,
        selectedIds,
        nudgeStep: theme.nudgeStep,
        nudgeStepFast: theme.nudgeStepFast,
        onSelectAll: selection.selectAll,
        onClearSelection: selection.clearSelection,
        onGroup: layers.groupSelection,
        onUngroup: layers.ungroupSelection,
        onEscape: () => {
            if (inlineEdit.inlineEdit) {
                inlineEdit.cancelInlineEdit()
                return true
            }
            return false
        },
    })

    return {
        ...canvas,
        ...selection,
        ...elementOpsPublic,
        ...transform,
        ...layers,
        elements,
        selectedIds,
        setSelectedIds,
        pushHistorySnapshot,
        applyDocument,
        guides: guides.guides,
        canUndo: history.canUndo,
        canRedo: history.canRedo,
        undo: history.undo,
        redo: history.redo,
        copySelection: history.copySelection,
        pasteClipboard: history.pasteClipboard,
        deleteSelection: history.deleteSelection,
        duplicateSelection: history.duplicateSelection,
        inlineEdit,
    }
}
